import { api, Shipment, TelemetryReading, Alert, exportTelemetryCSV } from '../api';
import { eventTypeLabel, eventTypeIcon } from '../main';
import { statusBadge, severityBadge, timeAgo, darkLineChartOptions } from '@demo/ui';
import { generateComplianceReport } from '../report';

declare const Chart: any;

let tempChartInstance: any = null;
let humChartInstance: any = null;

export async function renderShipmentDetail(container: HTMLElement, id: string) {
  const [shipment, telemetry, alerts] = await Promise.all([
    api.getShipment(id),
    api.getShipmentTelemetry(id),
    api.getAlerts(),
  ]);

  const shipmentAlerts = alerts.filter(a => a.shipment_id === id);

  const tempInRange = shipment.current_temp != null &&
    shipment.current_temp >= shipment.temp_min &&
    shipment.current_temp <= shipment.temp_max;

  const progress = getProgress(shipment);

  const hasHumidityThresholds = shipment.humidity_min != null && shipment.humidity_max != null;

  container.innerHTML = `
    <div class="h-full overflow-y-auto page-enter">
      <div class="p-4 sm:p-6 max-w-6xl">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 sm:mb-8">
          <div class="flex items-center gap-3">
            <a href="#/shipments" class="text-th-text-3 hover:text-th-text transition-colors p-1 rounded-lg hover:bg-th-surface">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
            </a>
            <h1 class="text-xl sm:text-2xl font-bold tracking-tight">${shipment.id}</h1>
            ${statusBadge(shipment.status)}
          </div>
          <div class="flex items-center gap-2 sm:ml-auto">
            <button id="export-telemetry-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-th-surface hover:bg-th-surface-hover border border-th-border-2 text-th-text-2 text-sm font-medium rounded-xl transition-all hover:border-th-border-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
              <span class="hidden sm:inline">Export Telemetry</span><span class="sm:hidden">Export</span>
            </button>
            <button id="download-report-btn" class="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-sm font-medium rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
              Report
            </button>
          </div>
        </div>

        <!-- Route Hero -->
        <div class="bg-th-card rounded-2xl border border-th-border p-4 sm:p-7 mb-6">
          <div class="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0 mb-5">
            <div class="text-center">
              <div class="w-10 h-10 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-2">
                <div class="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <p class="text-base sm:text-lg font-bold">${shipment.origin}</p>
              <p class="text-[11px] text-th-text-3 uppercase tracking-wider font-medium">Origin</p>
            </div>
            <div class="w-full sm:flex-1 mx-0 sm:mx-10">
              <div class="relative">
                <div class="h-1.5 bg-th-surface rounded-full overflow-hidden">
                  <div class="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all shadow-sm shadow-blue-500/30" style="width: ${progress}%"></div>
                </div>
                <div class="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-[3px] border-blue-500 bg-th-bg shadow-lg shadow-blue-500/30 transition-all" style="left: calc(${progress}% - 8px)"></div>
              </div>
              <p class="text-xs text-th-text-3 text-center mt-3 font-medium">${progress.toFixed(0)}% complete</p>
            </div>
            <div class="text-center">
              <div class="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-2">
                <svg class="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg>
              </div>
              <p class="text-base sm:text-lg font-bold">${shipment.destination}</p>
              <p class="text-[11px] text-th-text-3 uppercase tracking-wider font-medium">Destination</p>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 text-sm text-th-text-3 pt-4 border-t border-th-border">
            <span class="flex items-center gap-2"><svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>Pickup: ${formatDate(shipment.pickup_time)}</span>
            <span class="flex items-center gap-2"><svg class="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>ETA: ${formatDate(shipment.eta)}</span>
          </div>
        </div>

        <!-- Info Grid -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div class="bg-th-card rounded-2xl border ${shipment.current_temp != null && !tempInRange ? 'border-red-500/30' : 'border-th-border'} p-5 stat-card">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-2 h-2 rounded-full ${shipment.current_temp != null ? (tempInRange ? 'bg-green-400' : 'bg-red-400 animate-pulse') : 'bg-th-text-4'}"></div>
              <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Temperature</p>
            </div>
            <p class="text-2xl font-bold ${shipment.current_temp != null ? (tempInRange ? 'text-green-400' : 'text-red-400') : 'text-th-text-3'}">${shipment.current_temp != null ? shipment.current_temp.toFixed(1) + '°C' : 'N/A'}</p>
            <p class="text-xs text-th-text-4 mt-1.5">Range: ${shipment.temp_min}° to ${shipment.temp_max}°C</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-5 stat-card">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-2 h-2 rounded-full bg-cyan-400"></div>
              <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Humidity</p>
            </div>
            <p class="text-2xl font-bold text-cyan-400">${shipment.current_humidity != null ? shipment.current_humidity.toFixed(0) + '%' : 'N/A'}</p>
            ${hasHumidityThresholds ? `<p class="text-xs text-th-text-4 mt-1.5">Range: ${shipment.humidity_min}% to ${shipment.humidity_max}%</p>` : '<p class="text-xs text-th-text-4 mt-1.5">Not monitored</p>'}
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-5 stat-card">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-2 h-2 rounded-full bg-amber-400"></div>
              <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Cargo</p>
            </div>
            <p class="text-base font-semibold">${shipment.cargo_type}</p>
            <p class="text-xs text-th-text-4 mt-1.5">${shipment.cargo_description}</p>
          </div>
          <div class="bg-th-card rounded-2xl border border-th-border p-5 stat-card">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-2 h-2 rounded-full bg-violet-400"></div>
              <p class="text-[11px] text-th-text-3 font-medium uppercase tracking-wider">Vehicle</p>
            </div>
            <a href="#/vehicles/${shipment.vehicle_id}" class="text-base font-semibold text-blue-400 hover:text-blue-300 transition-colors">${shipment.vehicle_name || shipment.vehicle_id}</a>
            <p class="text-xs text-th-text-4 mt-1.5 font-mono">${shipment.vehicle_id}</p>
          </div>
        </div>

        <!-- Temperature Chart -->
        <div class="bg-th-card rounded-2xl border border-th-border p-4 sm:p-6 mb-6">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h3 class="text-sm font-semibold text-th-text-3 uppercase tracking-wider">Temperature History</h3>
            <div class="flex items-center gap-1" id="temp-filter-btns">
              <button data-range="1" class="temp-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">1h</button>
              <button data-range="6" class="temp-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">6h</button>
              <button data-range="12" class="temp-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">12h</button>
              <button data-range="24" class="temp-filter-btn active px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors bg-blue-600 text-white">24h</button>
              <button data-range="0" class="temp-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">All</button>
            </div>
          </div>
          <div class="relative" style="height: 220px;">
            <canvas id="temp-chart"></canvas>
          </div>
          <div class="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-3 text-xs text-th-text-3">
            <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-cyan-400 inline-block rounded"></span> Temp</span>
            <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-red-400/50 inline-block rounded" style="border-top: 1px dashed rgba(248,113,113,0.7)"></span> Max (${shipment.temp_max}°C)</span>
            <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-blue-400/50 inline-block rounded" style="border-top: 1px dashed rgba(96,165,250,0.7)"></span> Min (${shipment.temp_min}°C)</span>
          </div>
        </div>

        <!-- Humidity Chart -->
        <div class="bg-th-card rounded-2xl border border-th-border p-4 sm:p-6 mb-6">
          <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
            <h3 class="text-sm font-semibold text-th-text-3 uppercase tracking-wider">Humidity History</h3>
            <div class="flex items-center gap-1" id="hum-filter-btns">
              <button data-range="1" class="hum-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">1h</button>
              <button data-range="6" class="hum-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">6h</button>
              <button data-range="12" class="hum-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">12h</button>
              <button data-range="24" class="hum-filter-btn active px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors bg-blue-600 text-white">24h</button>
              <button data-range="0" class="hum-filter-btn px-2.5 sm:px-3 py-1 text-xs rounded-lg transition-colors">All</button>
            </div>
          </div>
          <div class="relative" style="height: 220px;">
            <canvas id="hum-chart"></canvas>
          </div>
          <div class="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-3 text-xs text-th-text-3">
            <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-violet-400 inline-block rounded"></span> Humidity</span>
            ${hasHumidityThresholds ? `
              <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-red-400/50 inline-block rounded" style="border-top: 1px dashed rgba(248,113,113,0.7)"></span> Max (${shipment.humidity_max}%)</span>
              <span class="flex items-center gap-1.5"><span class="w-3 h-0.5 bg-blue-400/50 inline-block rounded" style="border-top: 1px dashed rgba(96,165,250,0.7)"></span> Min (${shipment.humidity_min}%)</span>
            ` : '<span class="text-th-text-4">No thresholds configured</span>'}
          </div>
        </div>

        <!-- Two columns: Telemetry + Alerts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="bg-th-surface rounded-xl border border-th-border-2 p-4 sm:p-5">
            <h3 class="text-sm font-semibold text-th-text-3 mb-4 uppercase tracking-wider">Telemetry History</h3>
            ${telemetry.length === 0 ? '<p class="text-th-text-3 text-sm">No telemetry data available</p>' : `
              <div class="space-y-2 max-h-80 overflow-y-auto">
                ${telemetry.slice(-15).reverse().map(t => `
                  <div class="flex items-center justify-between gap-2 py-2 border-b border-th-border text-xs sm:text-sm">
                    <span class="text-th-text-3">${formatTime(t.timestamp)}</span>
                    <span class="${t.temperature > shipment.temp_max || t.temperature < shipment.temp_min ? 'text-red-400' : 'text-green-400'}">${t.temperature.toFixed(1)}°</span>
                    <span class="text-blue-400">${t.humidity.toFixed(0)}%</span>
                    <span class="text-th-text-3">${t.speed.toFixed(0)}<span class="hidden sm:inline"> km/h</span></span>
                  </div>
                `).join('')}
              </div>
            `}
          </div>

          <div class="bg-th-surface rounded-xl border border-th-border-2 p-4 sm:p-5">
            <h3 class="text-sm font-semibold text-th-text-3 mb-4 uppercase tracking-wider">Events & Alerts</h3>
            ${shipmentAlerts.length === 0 ? '<p class="text-th-text-3 text-sm">No alerts for this shipment</p>' : `
              <div class="space-y-3">
                ${shipmentAlerts.map(a => `
                  <div class="flex items-start gap-3 p-3 rounded-lg ${a.resolved ? 'bg-th-surface' : 'bg-th-surface border border-th-border-2'}">
                    ${eventTypeIcon(a.event_type)}
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="text-sm font-medium">${eventTypeLabel(a.event_type)}</span>
                        ${severityBadge(a.severity)}
                        ${a.resolved ? '<span class="text-xs text-green-500">Resolved</span>' : ''}
                      </div>
                      <p class="text-xs text-th-text-3">${a.description}</p>
                      <p class="text-xs text-th-text-4 mt-1">${timeAgo(a.created_at)}</p>
                    </div>
                  </div>
                `).join('')}
              </div>
            `}
          </div>
        </div>
      </div>
    </div>
  `;

  telemetry.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  document.getElementById('export-telemetry-btn')?.addEventListener('click', () => exportTelemetryCSV(id));

  initTempChart(telemetry, shipment);
  initHumChart(telemetry, shipment);
  setupChartFilter('temp-filter-btns', 'temp-filter-btn', id, shipment, initTempChart);
  setupChartFilter('hum-filter-btns', 'hum-filter-btn', id, shipment, initHumChart);

  document.getElementById('download-report-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('download-report-btn')!;
    btn.setAttribute('disabled', 'true');
    btn.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg> Generating...`;
    try {
      let vehicle = null;
      try { vehicle = await api.getVehicle(shipment.vehicle_id); } catch {}
      const allTelemetry = await api.getShipmentTelemetry(id);
      allTelemetry.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      generateComplianceReport(shipment, allTelemetry, alerts, vehicle);
    } finally {
      btn.removeAttribute('disabled');
      btn.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg> Download Report`;
    }
  });
}

function initTempChart(telemetry: TelemetryReading[], shipment: Shipment) {
  if (tempChartInstance) {
    tempChartInstance.destroy();
    tempChartInstance = null;
  }

  const canvas = document.getElementById('temp-chart') as HTMLCanvasElement;
  if (!canvas || telemetry.length === 0) return;

  const labels = telemetry.map(t => new Date(t.timestamp));
  const temps = telemetry.map(t => t.temperature);

  tempChartInstance = new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Temperature',
          data: temps,
          borderColor: 'rgba(34, 211, 238, 0.9)',
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: 'rgba(34, 211, 238, 1)',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
        },
        {
          label: 'Max Threshold',
          data: labels.map(() => shipment.temp_max),
          borderColor: 'rgba(248, 113, 113, 0.5)',
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        },
        {
          label: 'Min Threshold',
          data: labels.map(() => shipment.temp_min),
          borderColor: 'rgba(96, 165, 250, 0.5)',
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
        },
      ],
    },
    options: darkLineChartOptions({ unit: '°C', thresholdMin: shipment.temp_min, thresholdMax: shipment.temp_max }),
  });
}

function initHumChart(telemetry: TelemetryReading[], shipment: Shipment) {
  if (humChartInstance) {
    humChartInstance.destroy();
    humChartInstance = null;
  }

  const canvas = document.getElementById('hum-chart') as HTMLCanvasElement;
  if (!canvas || telemetry.length === 0) return;

  const labels = telemetry.map(t => new Date(t.timestamp));
  const humidities = telemetry.map(t => t.humidity);

  const datasets: any[] = [
    {
      label: 'Humidity',
      data: humidities,
      borderColor: 'rgba(167, 139, 250, 0.9)',
      borderWidth: 2,
      fill: false,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: 'rgba(167, 139, 250, 1)',
      pointHoverBorderColor: '#fff',
      pointHoverBorderWidth: 2,
    },
  ];

  if (shipment.humidity_max != null) {
    datasets.push({
      label: 'Max Threshold',
      data: labels.map(() => shipment.humidity_max),
      borderColor: 'rgba(248, 113, 113, 0.5)',
      borderWidth: 1.5,
      borderDash: [6, 4],
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
    });
  }
  if (shipment.humidity_min != null) {
    datasets.push({
      label: 'Min Threshold',
      data: labels.map(() => shipment.humidity_min),
      borderColor: 'rgba(96, 165, 250, 0.5)',
      borderWidth: 1.5,
      borderDash: [6, 4],
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
    });
  }

  humChartInstance = new Chart(canvas, {
    type: 'line',
    data: { labels, datasets },
    options: darkLineChartOptions({
      unit: '%',
      thresholdMin: shipment.humidity_min ?? undefined,
      thresholdMax: shipment.humidity_max ?? undefined,
    }),
  });
}

function setupChartFilter(
  containerId: string,
  btnClass: string,
  shipmentId: string,
  shipment: Shipment,
  renderFn: (telemetry: TelemetryReading[], shipment: Shipment) => void,
) {
  const btns = document.querySelectorAll(`.${btnClass}`);
  const inactiveClass = `${btnClass} px-3 py-1 text-xs rounded-lg transition-colors text-th-text-3 hover:text-th-text hover:bg-th-surface`;
  const activeClass = `${btnClass} active px-3 py-1 text-xs rounded-lg transition-colors bg-blue-600 text-white`;

  btns.forEach(btn => {
    btn.addEventListener('click', async () => {
      btns.forEach(b => { b.className = inactiveClass; });
      btn.className = activeClass;

      const hours = parseInt((btn as HTMLElement).dataset.range || '0');
      let from: string | undefined;
      if (hours > 0) {
        from = utcString(new Date(Date.now() - hours * 60 * 60 * 1000));
      }

      const telemetry = await api.getShipmentTelemetry(shipmentId, from);
      telemetry.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      renderFn(telemetry, shipment);
    });
  });

  btns.forEach(btn => {
    if (!btn.classList.contains('active')) {
      btn.className = inactiveClass;
    }
  });
}

function getProgress(s: Shipment): number {
  if (s.status === 'delivered') return 100;
  if (s.status === 'scheduled') return 0;
  if (s.status === 'loading') return 5;
  if (s.status === 'cancelled') return 0;
  const pickup = new Date(s.pickup_time).getTime();
  const eta = new Date(s.eta).getTime();
  const now = Date.now();
  if (eta <= pickup) return 50;
  const pct = ((now - pickup) / (eta - pickup)) * 100;
  return Math.max(5, Math.min(95, pct));
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function utcString(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}
